/**
 * The upload machinery shared by the creator page and the studio's Upload page: drop zone, chunked
 * resumable uploads that survive drops, the filed-as chips and the Received / In the studio table. On the
 * studio's Upload page the "Tag this drop" modal opens the moment a drop's files start landing, so tags are
 * picked while the upload runs, and every clip's row menu reopens it later.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AUDIO_MIME_BY_EXT, IMAGE_MIME_BY_EXT, UPLOAD_MAX_BYTES, UPLOAD_QUIET_ABOVE, VIDEO_MIME_BY_EXT, WORK_POLL_MS, analyseDurationWords, isJunkFilename, transcribeSeconds, uploadKindOf } from '@studio/contracts';
import { filesFromDrop, filesFromInput, type DroppedFile } from '@/lib/drop-files';
import { AnimateConfirm, type AnimateTarget } from '@/components/animate-still';
import { useComposerActions } from '@/components/composer-actions';
import { Icon } from '@/components/icons';
import { HeldClip } from '@/components/held-clip';
import { PartsModal } from '@/components/parts-modal';
import { RowMenu } from '@/components/row-menu';
import { TagAssetsModal } from '@/components/tag-assets-modal';
import { fmtBytes } from '@/lib/format';
import { AnalyseProgress } from '@/routes/analyse';
import { entryKey, preparePhotos, sendEntries, uploadSpec } from '@/lib/send-drop';
import { holdTabOpen } from '@/lib/upload';
import { queryClient, trpc } from '@/lib/trpc';

interface ActiveUpload {
  assetId: string;
  batchId: string;
  /** A sound: the whole folder shows on its drop's one row. */
  audio: boolean;
  sent: number;
  total: number;
  failed: boolean;
}

const GIB = 1024 * 1024 * 1024;
const ALL_MIME_BY_EXT: Record<string, string> = { ...VIDEO_MIME_BY_EXT, ...IMAGE_MIME_BY_EXT, ...AUDIO_MIME_BY_EXT };

/** Whether a drop is analysed after it lands, remembered per browser. */
const ANALYSE_AFTER_KEY = 'studio-upload-analyse-after';

export function UploadPanel({ brandName, batchTagging, owner = false }: { brandName: string | null; batchTagging?: boolean; owner?: boolean }) {
  const navigate = useNavigate();
  const composer = useComposerActions();
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const rename = useMutation(trpc.assets.rename.mutationOptions({
    onSuccess: async () => {
      setRenaming(null);
      await queryClient.invalidateQueries({ queryKey: trpc.upload.mine.queryKey() });
    },
  }));
  const profile = useQuery(trpc.upload.profile.queryOptions());
  // A landed clip is analysing, so the table re-reads at the Analyser's cadence until every row has
  // finished, then stops polling altogether. It used to poll every 15 s whatever was happening, which
  // left a finished analysis showing as still running long after the Analyser had moved on, and left
  // an idle table asking forever (Dax, 2026-09-20). New work arrives by invalidation, not by polling.
  const mine = useQuery(trpc.upload.mine.queryOptions(undefined, {
    refetchInterval: (q) => (q.state.data?.some((r) => (r.status === 'awaiting_upload' && r.bytes !== null) || (r.status === 'indexed' && r.progressStage !== null) || (r.audio !== null && r.audio.ready < r.audio.files - r.audio.unsent)) ? WORK_POLL_MS : false),
    refetchOnWindowFocus: true,
  }));
  const create = useMutation(trpc.upload.create.mutationOptions());
  const append = useMutation(trpc.upload.append.mutationOptions());
  const cancel = useMutation(trpc.upload.cancel.mutationOptions());
  const checkDup = useMutation(trpc.upload.checkDuplicates.mutationOptions());
  const removeOld = useMutation(trpc.library.remove.mutationOptions());
  const removeOldSound = useMutation(trpc.compose.removeBed.mutationOptions());
  const [active, setActive] = useState<Record<string, ActiveUpload>>({});
  const [dragOver, setDragOver] = useState(false);
  // Off brings clips in without analysis, ready for Composer Fast. Remembered in this browser; on until someone says otherwise.
  const [analyseAfter, setAnalyseAfter] = useState(() => {
    try {
      return localStorage.getItem(ANALYSE_AFTER_KEY) !== 'off';
    } catch {
      return true;
    }
  });
  const chooseAnalyse = (on: boolean) => {
    setAnalyseAfter(on);
    try {
      localStorage.setItem(ANALYSE_AFTER_KEY, on ? 'on' : 'off');
    } catch {
      // Storage unavailable: the choice holds for this visit.
    }
  };
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // The drop the person asked to cancel, waiting on their confirmation.
  const [cancelling, setCancelling] = useState<{ batchId: string; audio: boolean } | null>(null);
  // Each drop this tab is sending, by batch, so Cancel can stop it mid-file.
  const senders = useRef(new Map<string, AbortController>());
  const [dupes, setDupes] = useState<{ name: string; assetId: string; displayName: string; kind: 'video' | 'image' | 'audio'; replace: boolean }[] | null>(null);
  const [animating, setAnimating] = useState<AnimateTarget | null>(null);
  const [partsOf, setPartsOf] = useState<string | null>(null);
  // The tag modal: the video clips of the drop that just started landing, or one clip from its row menu.
  const [tagging, setTagging] = useState<{ assetIds: string[]; drop: boolean; batchId?: string | null; fileCount?: number; facets: boolean } | null>(null);
  const pendingRef = useRef<DroppedFile[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  // "Add files…" on a clip's row: the picker, and the clip the chosen files join.
  const addInput = useRef<HTMLInputElement>(null);
  const addTarget = useRef<{ assetId: string; name: string } | null>(null);
  const addFilesTo = (target: { assetId: string; name: string }) => {
    addTarget.current = target;
    addInput.current?.click();
  };
  // Long clips analysed without their speech (Dax, 2026-10-06): transcribe one, or switch the brand to do it always.
  const transcribe = useMutation(trpc.assets.transcribe.mutationOptions({
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: trpc.upload.mine.queryKey() }),
    onError: (err) => setError(err.message),
  }));
  const speechDefaults = useQuery({ ...trpc.brandSettings.analyseDefaults.queryOptions(), enabled: Boolean(batchTagging) });
  const setTranscribeLong = useMutation(trpc.brandSettings.setTranscribeLong.mutationOptions({
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: trpc.brandSettings.analyseDefaults.queryKey() }),
  }));
  const discard = useMutation(trpc.upload.discard.mutationOptions({
    onSuccess: async (_data, { assetId }) => {
      setActive((a) => Object.fromEntries(Object.entries(a).filter(([, u]) => u.assetId !== assetId)));
      await queryClient.invalidateQueries({ queryKey: trpc.upload.mine.queryKey() });
    },
    onError: (err) => setError(err.message),
  }));

  const accept = (files: DroppedFile[]): DroppedFile[] => {
    const ok: DroppedFile[] = [];
    // A pack folder carries readmes, artwork and .DS_Store; those are skipped in silence, since the
    // person dropped a folder, not each file, and does not need telling about every one of them.
    const walked = files.some((f) => f.path !== null);
    // A sound pack's artwork is not a still, so a drop with sounds in it leaves its pictures out.
    const sounds = files.some((f) => uploadKindOf(f.file.name) === 'audio');
    for (const f of files) {
      const ext = f.file.name.slice(f.file.name.lastIndexOf('.') + 1).toLowerCase();
      if (sounds && uploadKindOf(f.file.name) === 'image') continue;
      if (!ALL_MIME_BY_EXT[ext]) {
        if (!walked) setError(`${f.file.name}: not a supported file (${Object.keys(ALL_MIME_BY_EXT).join(', ')})`);
      } else if (f.file.size > UPLOAD_MAX_BYTES) setError(`${f.file.name}: larger than ${UPLOAD_MAX_BYTES / GIB} GB`);
      else ok.push(f);
    }
    return ok;
  };

  const start = async (picked: DroppedFile[]): Promise<void> => {
    const files = accept(picked);
    if (!files.length) {
      // A folder whose files are all readmes and artwork looks like nothing happened otherwise.
      if (picked.length && picked.some((f) => f.path !== null)) {
        setError(`Nothing in that folder is a supported file (${Object.keys(ALL_MIME_BY_EXT).join(', ')})`);
      }
      return;
    }
    setError(null);
    setNotice(null);
    await checkAndProceed(files);
  };

  const checkAndProceed = async (files: DroppedFile[]): Promise<void> => {
    // Pre-flight: filenames the brand already holds, video or sound, come back for a skip-or-replace
    // decision before a byte is sent, so a skipped file is never uploaded or analysed. A sound starts
    // at skip: replacing it throws away its listen, and a folder dropped again is mostly files already here.
    try {
      const found = await checkDup.mutateAsync({ files: files.map((f) => ({ name: f.file.name, path: f.path })) });
      if (found.length) {
        pendingRef.current = files;
        setDupes(found.map((d) => ({ name: d.originalFilename, assetId: d.assetId, displayName: d.displayName, kind: d.kind, replace: d.kind !== 'audio' })));
        return;
      }
    } catch (err) {
      setError((err as Error).message);
      return;
    }
    await proceed(files);
  };

  /** The user's per-file skip/replace answers applied: replacements delete the old asset first. */
  const resolveDupes = async (): Promise<void> => {
    const decided = dupes ?? [];
    const replaceIds = decided.filter((x) => x.replace && x.kind !== 'audio').map((x) => x.assetId);
    const replaceSounds = decided.filter((x) => x.replace && x.kind === 'audio').map((x) => x.assetId);
    const skip = new Set(decided.filter((x) => !decided.some((y) => y.name === x.name && y.replace)).map((x) => x.name));
    const files = pendingRef.current.filter((f) => !skip.has(f.file.name));
    setDupes(null);
    pendingRef.current = [];
    if (!files.length) return;
    try {
      if (replaceIds.length) await removeOld.mutateAsync({ assetIds: replaceIds });
      for (const bedId of replaceSounds) await removeOldSound.mutateAsync({ bedId });
    } catch (err) {
      setError(`Could not replace: ${(err as Error).message}`);
      return;
    }
    await proceed(files);
  };

  /** One upload's progress changed; a cancelled drop's uploads are gone and stay gone. */
  const patchActive = (assetId: string, change: Partial<ActiveUpload>) => {
    setActive((a) => (a[assetId] ? { ...a, [assetId]: { ...a[assetId]!, ...change } } : a));
  };

  /**
   * Register the drop (or, with `target`, the files added to an existing clip), then send it. A new drop opens
   * the tag modal while it lands; files added to a clip simply join it.
   */
  const proceed = async (dropped: DroppedFile[], target?: { assetId: string; name: string }): Promise<void> => {
    const release = holdTabOpen();
    const controller = new AbortController();
    try {
      const stillCount = dropped.filter((f) => uploadKindOf(f.file.name) === 'image').length;
      if (stillCount) setNotice(`Preparing ${stillCount} photo${stillCount === 1 ? '' : 's'}…`);
      const files = await preparePhotos(dropped);
      if (stillCount) setNotice(null);
      // The whole drop is registered first, so its row counts every file, then sent a page at a time.
      const specs = files.map(uploadSpec);
      const registered = target ? await append.mutateAsync({ assetId: target.assetId, files: specs }) : await create.mutateAsync({ files: specs, autoAnalyse: batchTagging ? analyseAfter : true });
      const held = registered.entries.filter((e) => e === null).length;
      if (held) setNotice(registered.batchId === null ? `All ${held} files are already in the library.` : `${held} already in the library; sending the other ${files.length - held}.`);
      if (target) setNotice(`Adding ${files.length} file${files.length === 1 ? '' : 's'} to "${target.name}"…`);
      const batchId = registered.batchId;
      if (batchId === null) return;
      senders.current.set(batchId, controller);
      const sending = registered.entries.flatMap((entry, i) => (entry ? [{ entry, file: files[i]!.file }] : []));
      await queryClient.invalidateQueries({ queryKey: trpc.upload.mine.queryKey() });
      if (!target) {
        // Editors tag the drop (actor, creator, brand and friends) while it lands; the uploads never wait.
        // A clip or a still is whatever the server filed with a part; a video dropped among sounds is a sound.
        const filed = sending.filter((x) => x.entry.partId !== null);
        const videoIds = filed.filter((x) => uploadKindOf(x.file.name) === 'video').map((x) => x.entry.assetId);
        const shootIds = filed.map((x) => x.entry.assetId);
        // Several video files carry a question (one project, or separate clips?) that the modal asks while
        // they land; editors tag the drop in the same modal (its stills with it), creators only answer the question.
        const question = videoIds.length > 1 ? batchId : null;
        if (shootIds.length && (batchTagging || question)) setTagging({ assetIds: batchTagging ? shootIds : videoIds, drop: true, batchId: question, fileCount: question ? videoIds.length : shootIds.length, facets: Boolean(batchTagging) });
      }
      // A pack is hundreds of files: refreshing the table after every one of them is hundreds of
      // round trips for a row nobody is reading yet. Once at the end, and every so often on the way.
      const chatty = sending.length <= UPLOAD_QUIET_ABOVE;
      // Every file is registered up front, so the drop's row is on the table before its files are sent.
      setActive((a) => ({
        ...a,
        ...Object.fromEntries(sending.map((x) => [entryKey(x.entry), { assetId: x.entry.assetId, batchId, audio: x.entry.partId === null, sent: 0, total: x.file.size, failed: false }])),
      }));
      let done = 0;
      await sendEntries(sending, {
        signal: controller.signal,
        onProgress: (entry, sent) => patchActive(entryKey(entry), { sent }),
        onFailed: (entry) => patchActive(entryKey(entry), { failed: true }),
        onLanded: async (entry) => {
          // An audio file stays until its drop is sent, so the drop's row totals every file, not the rest.
          if (entry.partId !== null) {
            setActive((a) => {
              const { [entryKey(entry)]: finished, ...rest } = a;
              void finished;
              return rest;
            });
          }
          done += 1;
          if (chatty || done % UPLOAD_QUIET_ABOVE === 0) await queryClient.invalidateQueries({ queryKey: trpc.upload.mine.queryKey() });
        },
      });
      setActive((a) => Object.fromEntries(Object.entries(a).filter(([, u]) => u.batchId !== batchId || !u.audio || u.failed)));
      if (target) setNotice(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: trpc.upload.mine.queryKey() }),
        queryClient.invalidateQueries({ queryKey: trpc.library.pathKey() }),
      ]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      for (const [id, c] of senders.current) if (c === controller) senders.current.delete(id);
      release();
    }
  };

  /** Cancel, confirmed: stop sending, then the server removes the drop. */
  const cancelDrop = async (batchId: string): Promise<void> => {
    setCancelling(null);
    senders.current.get(batchId)?.abort();
    senders.current.delete(batchId);
    setActive((a) => Object.fromEntries(Object.entries(a).filter(([, u]) => u.batchId !== batchId)));
    setTagging((t) => (t?.batchId === batchId ? null : t));
    try {
      await cancel.mutateAsync({ batchId });
    } catch (err) {
      setError((err as Error).message);
    }
    await queryClient.invalidateQueries({ queryKey: trpc.upload.mine.queryKey() });
  };

  const rights = profile.data?.rightsUntil ? new Date(profile.data.rightsUntil).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : null;

  return (
    <>
      <div
        className={`dropzone${dragOver ? ' over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); void filesFromDrop(e.dataTransfer).then(start); }}
      >
        <button type="button" className="dz-icon" aria-label="Choose files from your device" onClick={() => fileInput.current?.click()}><Icon name="upload" /></button>
        <div className="dz-line">
          Drag files in, or{' '}
          <button type="button" className="dz-pick" onClick={() => fileInput.current?.click()}>choose from your device</button>
        </div>
        <div className="sub dz-note">Video (MP4, MOV), photos (JPG, PNG, HEIC) or audio (MP3, WAV, M4A) · up to {UPLOAD_MAX_BYTES / GIB} GB each · uploads resume if the connection drops · audio lands in the audio library · drop a folder and its names become tags</div>
        {batchTagging && (
          <label className="row dz-analyse">
            <input type="checkbox" checked={analyseAfter} onChange={(e) => chooseAnalyse(e.target.checked)} />
            <span>Analyse after upload</span>
          </label>
        )}
        <input
          ref={fileInput}
          type="file"
          hidden
          multiple
          accept={Object.values(ALL_MIME_BY_EXT).join(',')}
          aria-label="Choose clips to upload"
          onChange={(e) => { void start(filesFromInput(e.target.files)); e.target.value = ''; }}
        />
        <input
          ref={addInput}
          type="file"
          hidden
          multiple
          accept={Object.values({ ...VIDEO_MIME_BY_EXT, ...IMAGE_MIME_BY_EXT }).join(',')}
          aria-label="Choose files to add to this clip"
          onChange={(e) => {
            const target = addTarget.current;
            const picked = filesFromInput(e.target.files).filter((f) => uploadKindOf(f.file.name) === 'video' || uploadKindOf(f.file.name) === 'image');
            e.target.value = '';
            if (target && picked.length) { setError(null); void proceed(picked, target); }
          }}
        />
      </div>
      <div className="creator-chips">
        <span className="label">Filed automatically as</span>
        {brandName && <span className="chip acc">{brandName}</span>}
        {profile.data?.name && <span className="chip acc">Creator · {profile.data.name}</span>}
        {rights && <span className="chip acc">{profile.data?.country ?? ''}{profile.data?.country ? ' · ' : ''}rights to {rights}</span>}
        <span className="sub creator-chips-note">Flavour and format are read from the file once it lands</span>
      </div>
      {error && <div className="card pad error" role="alert">{error}</div>}
      {notice && <div className="card pad sub" role="status">{notice}</div>}
      <div className="card creator-table">
        <table className="tbl">
          <thead><tr><th>File</th><th>Uploaded</th><th className="n">Size</th><th>Library</th><th>Analysed</th><th className="n">Segmented</th><th>Original file</th><th className="menu-cell" aria-label="Actions" /></tr></thead>
          <tbody>
            {mine.data?.map((row) => {
              // A project's row aggregates the progress of every file in its drop; a separate clip shows its own.
              const own = Object.values(active).find((u) => u.assetId === row.id);
              const batchUploads = row.audio ? Object.values(active).filter((u) => u.audio && u.batchId === row.batchId)
                : row.partCount > 1 && row.batchId ? Object.values(active).filter((u) => u.batchId === row.batchId) : own ? [own] : [];
              const up = batchUploads.length ? { assetId: row.id, batchId: row.batchId, sent: batchUploads.reduce((n, u) => n + u.sent, 0), total: batchUploads.reduce((n, u) => n + u.total, 0), failed: batchUploads.some((u) => u.failed) } : undefined;
              const uploading = Boolean(up && !up.failed) || (row.status === 'awaiting_upload' && row.bytes === null);
              // Not landed and not uploading from this tab: abandoned, or failed here.
              const discardable = row.status === 'awaiting_upload' && row.bytes === null && !(up && !up.failed);
              // An audio drop's files that are here and still being measured or named; unsent ones are not coming.
              const audioWorking = row.audio !== null && row.audio.ready < row.audio.files - row.audio.unsent;
              // A drop can be cancelled by whoever started it while it is still sending or being named.
              const cancellable = row.batchId !== null && (row.audio ? row.audio.mine && (uploading || audioWorking || row.audio.unsent > 0) : Boolean(up && !up.failed));
              const cancelItem = cancellable ? [null, { label: 'Cancel upload', danger: true, onClick: () => setCancelling({ batchId: row.batchId!, audio: row.audio !== null }) }] : [];
              // Video rows open in the Analyser on click, stills in Tagging; the pencil appears once the analysis has named it.
              const isVideo = row.status !== null;
              const isStill = row.mediaKind === 'image';
              const named = isVideo && row.status !== 'awaiting_upload';
              const openAnalyser = () => void navigate(isStill ? { to: '/tagging', search: { asset: row.id } } : { to: '/analyse', search: { asset: row.id } });
              // An audio drop is a pack: a click opens Browse on just its sounds (2026-10-04).
              const packId = !isVideo ? row.batchId : null;
              const openPack = () => { if (packId) void navigate({ to: '/browse', search: { tab: 'audio', pack: packId } }); };
              return (
                <tr key={row.id} className={isVideo || packId ? 'row-open' : ''} onClick={isVideo ? openAnalyser : packId ? openPack : undefined}>
                  <td>
                    <span className="row name-cell">
                      {row.status === 'awaiting_upload' && isJunkFilename(row.name) ? <span className="sub">Named after analysis</span> : <b>{row.name}</b>}
                      {row.partCount > 1 && (batchTagging ? (
                        <button type="button" className="chip chip-btn lib-parts" title={`A project of ${row.partCount} files, one clip: see the files`}
                          onClick={(e) => { e.stopPropagation(); setPartsOf(row.id); }}><Icon name="folder" /> {row.partCount} files</button>
                      ) : <span className="chip lib-parts" title={`A project of ${row.partCount} files, one clip`}><Icon name="folder" /> {row.partCount} files</span>)}
                      {row.pendingDecision && <span className="chip" title="Say whether this drop is one project or separate clips">awaiting your answer</span>}
                      {named && (
                        <button type="button" className="name-edit" title="Rename" aria-label={`Rename ${row.name}`}
                          onClick={(e) => { e.stopPropagation(); setRenaming({ id: row.id, name: row.name }); }}>
                          <Icon name="pencil" />
                        </button>
                      )}
                    </span>
                  </td>
                  <td className="sub nowrap">{when(row.createdAt)}</td>
                  <td className="n nowrap">{fmtBytes(up?.total ?? row.bytes)}</td>
                  <td>
                    {up?.failed ? <span className="chip warn">Failed, drop it in again to resume</span>
                      : uploading ? <span className="chip">{up && up.total ? `Uploading… ${Math.round((100 * up.sent) / up.total)}%` : 'Uploading…'}</span>
                        : audioWorking ? <span className="chip acc">Processing</span>
                          : row.audio?.unsent ? <span className="chip warn" title="These files never finished sending. Drop the folder in again; sounds already in the library are skipped.">{row.audio.unsent} not received</span>
                          : row.status === null ? <span className="chip ok">Audio library</span>
                          : row.status === 'indexed' ? <span className="chip ok">In library</span>
                            : row.status === 'stored' ? <span className="chip" title="File saved, not analysed">Stored</span>
                              : <span className="chip acc">Processing</span>}
                  </td>
                  <td>
                    {row.heldReason ? <HeldClip reason={row.heldReason} assetId={row.id} durationMs={row.durationMs} estimateMs={row.heldEstimateMs} />
                      : up && !up.failed ? <progress className="upbar" value={up.sent} max={up.total} aria-label="Upload progress" />
                      : row.status === 'awaiting_upload' && row.bytes !== null && !row.pendingDecision ? (
                        <AnalyseProgress stage={row.progressStage} progress={row.analyseProgress} etaAt={row.analyseEtaAt} />
                      ) : row.status === 'indexed' && row.progressStage ? (
                        <AnalyseProgress stage={row.progressStage} progress={row.analyseProgress} etaAt={row.analyseEtaAt} />
                      ) : row.audio && audioWorking ? (
                        // A drop of sounds lands at once but is measured, then listened to and named, one by one.
                        <AnalyseProgress stage={`${row.audio.measuring ? 'Measuring' : 'Naming'} · ${row.audio.ready} of ${row.audio.files - row.audio.unsent}`} progress={row.audio.ready / (row.audio.files - row.audio.unsent)} />
                      ) : row.audio ? (row.audio.unsent < row.audio.files ? <span className="chip ok">Yes</span> : null)
                      : row.status === 'stored' ? <span className="sub" title="File saved, not analysed">Not yet</span>
                        : row.status !== null && row.status !== 'awaiting_upload' ? <span className="chip ok">Yes</span> : null}
                  </td>
                  <td className="n mono">{row.keptSnippets || ''}</td>
                  <td className="sub nowrap orig-file" title={row.originalFilename ?? undefined}>{row.originalFilename ?? ''}</td>
                  <td className="menu-cell" onClick={(e) => e.stopPropagation()}>
                    {isVideo && isStill && (
                      <RowMenu title="Photo actions" items={[
                        { label: 'Open in Tagging', onClick: openAnalyser },
                        ...(batchTagging ? [{ label: 'Tag this photo…', onClick: () => setTagging({ assetIds: [row.id], drop: false, facets: true }) }, { label: 'Animate this image', onClick: () => setAnimating({ id: row.id, name: row.name }), disabled: row.status !== 'indexed' }] : []),
                        null,
                        { label: 'Rename', onClick: () => setRenaming({ id: row.id, name: row.name }), disabled: !named },
                        ...(discardable ? [null, { label: 'Discard upload', danger: true, onClick: () => discard.mutate({ assetId: row.id }) }] : []),
                        ...cancelItem,
                      ]} />
                    )}
                    {isVideo && !isStill && (
                      <RowMenu title="Clip actions" items={[
                        { label: 'Open in the Analyser', onClick: openAnalyser },
                        { label: 'Open in Tagging', onClick: () => void navigate({ to: '/tagging', search: { asset: row.id } }) },
                        ...(batchTagging ? [{ label: 'Tag this clip…', onClick: () => setTagging({ assetIds: [row.id], drop: false, facets: true }) }] : []),
                        { label: 'Send to the Composer', onClick: () => composer.sendAsset(row.id), disabled: !named },
                        ...(batchTagging ? [{ label: row.partCount > 1 ? 'Add files to this project…' : 'Add files…', onClick: () => addFilesTo({ assetId: row.id, name: row.name }), disabled: !named }] : []),
                        ...(batchTagging && row.untranscribed && row.status === 'indexed' ? [
                          { label: `Transcribe speech · ${analyseDurationWords(transcribeSeconds(row.durationMs ?? 0) * 1000)}`, onClick: () => transcribe.mutate({ assetId: row.id }), disabled: row.progressStage !== null },
                          ...(owner && speechDefaults.data && !speechDefaults.data.explicit.transcribeLong
                            ? [{ label: 'Transcribe long clips automatically', onClick: () => setTranscribeLong.mutate({ on: true }) }] : []),
                        ] : []),
                        null,
                        { label: 'Rename', onClick: () => setRenaming({ id: row.id, name: row.name }), disabled: !named },
                        ...(discardable ? [null, { label: 'Discard upload', danger: true, onClick: () => discard.mutate({ assetId: row.id }) }] : []),
                        ...cancelItem,
                      ]} />
                    )}
                    {!isVideo && (packId || cancellable) && (
                      <RowMenu title="Pack actions" items={[
                        ...(packId ? [
                          { label: 'Open in Browse', onClick: openPack },
                          { label: 'Open in Library', onClick: () => void navigate({ to: '/library', search: { media: 'audio', pack: packId } }) },
                        ] : []),
                        ...(packId ? cancelItem : cancelItem.slice(1)),
                      ]} />
                    )}
                  </td>
                </tr>
              );
            })}
            {mine.data?.length === 0 && <tr><td colSpan={8} className="sub creator-empty">Nothing uploaded yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {renaming && (
        <>
          <button type="button" className="overlay overlay-btn" aria-label="Close" onClick={() => setRenaming(null)} />
          <div className="modal managed-modal" role="dialog" aria-label="Rename clip">
            <h2>Rename clip</h2>
            <label className="field">
              <input autoFocus value={renaming.name} aria-label="Display name" maxLength={120}
                onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && renaming.name.trim()) rename.mutate({ assetId: renaming.id, name: renaming.name.trim() });
                  if (e.key === 'Escape') setRenaming(null);
                }} />
            </label>
            <span className="sub">The display name only; the stored file keeps its original name for duplicate detection.</span>
            <div className="row between">
              <button type="button" className="btn quiet" onClick={() => setRenaming(null)}>Cancel</button>
              <button type="button" className="btn primary" disabled={rename.isPending || !renaming.name.trim()}
                onClick={() => rename.mutate({ assetId: renaming.id, name: renaming.name.trim() })}>
                {rename.isPending ? 'Saving…' : 'Rename'}
              </button>
            </div>
            {rename.isError && <div className="error" role="alert">{rename.error.message}</div>}
          </div>
        </>
      )}
      {cancelling && (
        <>
          <button type="button" className="overlay overlay-btn" aria-label="Close" onClick={() => setCancelling(null)} />
          <div className="modal managed-modal" role="alertdialog" aria-label="Cancel upload">
            <h2>Cancel this upload?</h2>
            <span className="sub">
              {cancelling.audio ? 'Every sound in this drop is removed, including those already sent.' : 'Clips not yet sent are discarded. Clips already sent stay in the Library.'}
            </span>
            <div className="row between">
              <button type="button" className="btn quiet" onClick={() => setCancelling(null)}>Keep it</button>
              <button type="button" className="btn danger" disabled={cancel.isPending} onClick={() => void cancelDrop(cancelling.batchId)}>Cancel upload</button>
            </div>
          </div>
        </>
      )}
      {animating && <AnimateConfirm still={animating} onClose={() => setAnimating(null)} />}
      {partsOf && <PartsModal assetId={partsOf} canRemove={owner} onClose={() => setPartsOf(null)} />}
      {tagging && <TagAssetsModal initialAnalyse={analyseAfter} assetIds={tagging.assetIds} drop={tagging.drop} batchId={tagging.batchId ?? null} fileCount={tagging.fileCount ?? tagging.assetIds.length} facets={tagging.facets} onClose={() => setTagging(null)} />}
      {dupes && (
        <>
          <button type="button" className="overlay overlay-btn" aria-label="Close" onClick={() => { setDupes(null); pendingRef.current = []; }} />
          <div className="modal dup-modal" role="dialog" aria-label="Files already in the library">
            <h2>Already in the library</h2>
            <span className="sub">
              {(() => {
                const held = new Set(dupes.map((d) => d.name));
                const sending = new Set(pendingRef.current.map((f) => f.file.name).filter((n) => !held.has(n) || dupes.some((d) => d.name === n && d.replace))).size;
                return `${held.size} of ${new Set(pendingRef.current.map((f) => f.file.name)).size} files are already here; ${sending} will upload.`;
              })()}
            </span>
            <span className="sub">Ticked files replace what is in the library (its tags and history go with it); unticked ones are skipped.</span>
            <div className="picker-list">
              {dupes.map((d, i) => (
                <label key={d.assetId} className="row dup-row">
                  <input type="checkbox" checked={d.replace} aria-label={`Replace ${d.name}`}
                    onChange={() => setDupes(dupes.map((x, j) => (j === i ? { ...x, replace: !x.replace } : x)))} />
                  <b className="dup-name">{d.name}</b>
                  <span className="sub dup-as">{d.kind === 'audio' ? 'sound' : d.kind === 'image' ? 'photo' : 'clip'} &quot;{d.displayName}&quot;</span>
                </label>
              ))}
            </div>
            <div className="row between">
              <div className="row">
                <button type="button" className="btn" onClick={() => setDupes(dupes.map((x) => ({ ...x, replace: false })))}>Skip all</button>
                <button type="button" className="btn" onClick={() => setDupes(dupes.map((x) => ({ ...x, replace: true })))}>Replace all</button>
              </div>
              <button type="button" className="btn primary" onClick={() => void resolveDupes()}>Continue</button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function when(iso: string): string {
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return sameDay ? `Today ${time}` : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
